/**
 * /admin/bexio/drafts — Bexio Rechnungsentwürfe (C5, UX split in C5.1).
 *
 * A dedicated page rather than another section inside AdminBexioPage.js, which is
 * already ~1700 lines (Regel 48).
 *
 * THREE ACTS, DELIBERATELY SEPARATE — this is the whole point of C5.1:
 *
 *   SELECT   clicking a row changes what you are looking at. Nothing else. No fetch,
 *            no status change, no write. C5 wired the preflight to an effect keyed on
 *            the selection, so merely looking at a row appeared to decide its fate;
 *            worse, it made the check-all button's counter go DOWN.
 *   CHECK    an explicit act: one row from the preview panel, or every candidate of
 *            the month from the workflow card. Read-only end to end.
 *   CREATE   admin only, one confirmed POST, no retry, no bulk.
 *
 * DATA SOURCES — three real endpoints, nothing invented:
 *   1. GET /invoice-draft-preview        candidate rows for the month
 *   2. GET /invoice-draft-requests       ledger STATE per tenancy+month
 *   3. GET /invoice-drafts/preflight     what the create WOULD produce, incl. `ready`
 *   + POST /invoice-drafts               the single real write, admin-only
 *
 * READINESS IS NOT COMPUTED HERE. `ready` comes only from (3). The page never infers
 * it from "contact ok + amount > 0 + config present" — that is precisely the mistake
 * this surface exists to avoid.
 *
 * STATE IS KEYED BY MONTH. Every preflight result is stored under
 * `${year}-${month}-${tenancyId}`. In C5 the key was the tenancy alone and the only
 * thing binding it to a month was a wholesale reset that ran AFTER the month load
 * resolved — so between switching to October and that load returning, September's
 * READY was still cached, still shown, and still able to arm the create button while
 * the POST carried the new period. The key is now the guard: a late response writes
 * into its own month's slot, where it is correct, and is invisible from any other.
 *
 * BORDERS — `border-input`, deliberately, NOT `border-border`. `--border` is declared
 * twice in index.css: once as an HSL triple for the shadcn tokens, and once as an
 * rgba() colour for the older `var(--border)` consumers. The rgba declaration sits in
 * the `:root, [data-theme="dark"]` block, which has the same specificity as
 * `[data-theme="light"]` but comes later, so it wins in BOTH themes. `border-border`
 * therefore compiles to `hsl(rgba(...))` — invalid — and border-color falls back to
 * currentColor, i.e. borders take the text colour in light AND dark. `--input` carries
 * the same palette value and is declared only once, so it flips correctly. This is a
 * pre-existing, repo-wide condition (47 other files use `border-border`); it is
 * reported rather than fixed here, because repairing `--border` touches shared CSS.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import BexioDraftReplaceConfirmModal from "../../../components/admin/bexio/BexioDraftReplaceConfirmModal";
import useBexioDraftProviderMissing from "./useBexioDraftProviderMissing";

import {
  BexioDraftCreateError,
  createBexioInvoiceDraft,
  fetchBexioDraftPreflight,
  fetchBexioDraftRequests,
  fetchBexioInvoiceDraftPreview,
} from "../../../api/adminData/bexio";
import { getTokenPayload } from "../../../authStore";
import BexioDraftCreateConfirmModal from "../../../components/admin/bexio/BexioDraftCreateConfirmModal";
import BexioDraftMonthWorkflow from "../../../components/admin/bexio/BexioDraftMonthWorkflow";
import BexioDraftPreviewPanel from "../../../components/admin/bexio/BexioDraftPreviewPanel";
import BexioDraftSummaryCards from "../../../components/admin/bexio/BexioDraftSummaryCards";
import BexioDraftTable from "../../../components/admin/bexio/BexioDraftTable";
import {
  ROW_STATUS,
  blockerText,
  rowStatusFromLedger,
} from "../../../components/admin/bexio/bexioDraftLabels";

/**
 * How many preflights may be in flight at once.
 *
 * The preflight costs ~16-19 indexed single-row SELECTs and exactly zero Bexio API
 * calls, so a month sweep is cheap — but the backend pool is 15 connections, and an
 * unbounded Promise.all over 30 candidates would spend most of it on one screen.
 */
const CHECK_CONCURRENCY = 3;

function isAdmin() {
  try {
    return getTokenPayload()?.role === "admin";
  } catch {
    return false;
  }
}

function firstOfMonthIso(year, month) {
  return `${year}-${String(month).padStart(2, "0")}-01`;
}

/** Month-scoped cache key. See the header note on why the month belongs in here. */
function checkKey(year, month, tenancyId) {
  return `${year}-${month}-${tenancyId}`;
}

export default function BexioDraftsPage() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);

  const [previewRows, setPreviewRows] = useState([]);
  const [ledgerByTenancy, setLedgerByTenancy] = useState({});
  const [ledgerIncomplete, setLedgerIncomplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  /** `${year}-${month}-${tenancyId}` -> {state: "checking"|"done"|"error", result?, error?} */
  const [checks, setChecks] = useState({});
  const [checkRun, setCheckRun] = useState(null); // {done, total} while a sweep runs

  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState("");
  const [confirmRow, setConfirmRow] = useState(null);
  const [creating, setCreating] = useState(false);


  // Guards one create intent from becoming two POSTs even if React re-renders
  // between the click and the state update.
  const inFlight = useRef(false);
  // Monotonic tokens. A month load or a sweep started before a month change must not
  // be able to write anything once a newer one has started.
  const loadGen = useRef(0);
  const checkGen = useRef(0);

  const isAdminUser = isAdmin();
  // Two independent reasons to refuse a create, and the UI has to tell them apart:
  // one is about who you are, the other about what we know.
  const createBlockedReason = !isAdminUser
    ? "role"
    : ledgerIncomplete
    ? "ledger"
    : null;
  const canCreate = createBlockedReason === null;

  const loadMonth = useCallback(async () => {
    const gen = ++loadGen.current;
    setLoading(true);
    setLoadError("");
    try {
      const [preview, ledger] = await Promise.all([
        fetchBexioInvoiceDraftPreview({ year, month }),
        fetchBexioDraftRequests({ rentPeriod: firstOfMonthIso(year, month) }),
      ]);
      if (gen !== loadGen.current) return; // a newer month is on screen

      setPreviewRows(Array.isArray(preview?.rows) ? preview.rows : []);

      const items = Array.isArray(ledger?.items) ? ledger.items : [];
      const map = {};
      for (const item of items) map[item.tenancy_id] = item;
      setLedgerByTenancy(map);

      // The ledger read is capped server-side. Past the cap we are missing rows, and
      // a tenancy whose draft already exists would look like a fresh candidate — an
      // enabled create button over an invoice that is already in Bexio. We cannot
      // page here without building a paging architecture this slice deliberately
      // does not include, so we fail closed instead and say so.
      const total = Number(ledger?.total);
      setLedgerIncomplete(Number.isFinite(total) && total > items.length);
    } catch (e) {
      if (gen !== loadGen.current) return;
      setLoadError(e?.message || "Daten konnten nicht geladen werden.");
      setPreviewRows([]);
      setLedgerByTenancy({});
      // Unknown ledger is not "complete ledger". Fail closed.
      setLedgerIncomplete(true);
    } finally {
      if (gen === loadGen.current) setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    loadMonth();
  }, [loadMonth]);

  // Provider-loss pair (check + replace). Extracted to its own hook — Regel 48.
  // Declared AFTER loadMonth so the memoized `onChanged` closes over the stable
  // useCallback identity instead of a fresh arrow on every render.
  // `loadMonth` is the single source of truth after either action: neither the
  // check nor the replacement updates a row optimistically.
  const providerMissing = useBexioDraftProviderMissing({ onChanged: loadMonth });

  // ── rows: preview + ledger + (month-keyed) check state ─────────────
  //
  // `allRows` covers the whole month and drives every counter. `rows` is the same
  // list narrowed by the search box and drives only the table — a search changes
  // what you are looking at, never what the month contains.
  const allRows = useMemo(() => {
    return previewRows.map((r) => {
      const ledger = ledgerByTenancy[r.tenancy_id];
      const entry = checks[checkKey(year, month, r.tenancy_id)];
      const ledgerStatus = rowStatusFromLedger(ledger);

      // Precedence: what already happened in Bexio outranks what could happen.
      let status = ledgerStatus;
      if (!status) {
        if (!entry) status = ROW_STATUS.NOT_CHECKED;
        else if (entry.state === "checking") status = ROW_STATUS.CHECKING;
        else if (entry.state === "error") status = ROW_STATUS.CHECK_ERROR;
        else status = entry.result?.ready ? ROW_STATUS.READY : ROW_STATUS.BLOCKED;
      }

      return {
        tenancyId: r.tenancy_id,
        reference: r.tenancy_reference_number,
        tenantName: r.tenant_name,
        amount: r.total_preview_amount_chf,
        contactStatus: r.contact_mapping_status,
        status,
        documentNr: ledger?.document_nr || null,
        // Historical identity of a Bexio document that was proven deleted. Present
        // only on provider_missing rows; the confirmation modal shows it so the
        // operator can see WHICH document is gone before creating a replacement.
        replacedBexioInvoiceId: ledger?.replaced_bexio_invoice_id ?? null,
        replacedDocumentNr: ledger?.replaced_document_nr || null,
        ledger,
      };
    });
  }, [previewRows, ledgerByTenancy, checks, year, month]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allRows;
    return allRows.filter(
      (r) =>
        String(r.reference || "").toLowerCase().includes(q) ||
        String(r.tenantName || "").toLowerCase().includes(q)
    );
  }, [allRows, search]);

  // ── checking ───────────────────────────────────────────────────────
  const writeCheck = useCallback((key, value) => {
    setChecks((prev) => ({ ...prev, [key]: value }));
  }, []);

  /**
   * Invalidate every check that is currently in flight.
   *
   * The month key alone is not enough. It stops a response landing under the WRONG
   * month, but not under the right one at the wrong time — and the dangerous case is
   * exactly that: a preflight issued before a create, resolving after it failed, and
   * writing its pre-attempt `ready: true` back over the deliberately cleared slot.
   * Bumping the generation makes every outstanding response a no-op, and dropping the
   * "checking" placeholders stops abandoned rows reading "Prüfung läuft …" forever.
   */
  const abandonChecksInFlight = useCallback(() => {
    checkGen.current += 1;
    setCheckRun(null);
    setChecks((prev) => {
      const next = {};
      for (const [k, v] of Object.entries(prev)) {
        if (v?.state !== "checking") next[k] = v;
      }
      return next;
    });
  }, []);

  /** One read-only preflight. Never creates anything. */
  const checkOne = useCallback(
    async (tenancyId) => {
      const gen = checkGen.current;
      const y = year;
      const m = month;
      const key = checkKey(y, m, tenancyId);
      writeCheck(key, { state: "checking" });
      try {
        const data = await fetchBexioDraftPreflight({
          tenancyId,
          year: y,
          month: m,
        });
        if (gen !== checkGen.current) return null; // superseded — see abandonChecksInFlight
        writeCheck(key, { state: "done", result: data });
        return data;
      } catch (e) {
        if (gen !== checkGen.current) return null;
        // One row's failure is one row's failure. It must not look like a verdict
        // on the draft, and it must not become READY by omission.
        writeCheck(key, {
          state: "error",
          error: e?.message || "Prüfung fehlgeschlagen.",
        });
        return null;
      }
    },
    [year, month, writeCheck]
  );

  /**
   * Check every candidate of the month, read-only, at bounded concurrency.
   *
   * A single row failing does not stop the sweep — the run reports what it found,
   * and the failed row carries its own error. No POST happens on any path here.
   */
  const checkAll = useCallback(async () => {
    const gen = ++checkGen.current;
    const y = year;
    const m = month;
    const targets = previewRows.map((r) => r.tenancy_id).filter(Boolean);
    if (targets.length === 0) return;

    setCheckRun({ done: 0, total: targets.length });
    setChecks((prev) => {
      const next = { ...prev };
      for (const t of targets) next[checkKey(y, m, t)] = { state: "checking" };
      return next;
    });

    let cursor = 0;
    let ready = 0;
    let blocked = 0;
    let failed = 0;

    const worker = async () => {
      for (;;) {
        if (gen !== checkGen.current) return;
        const index = cursor++;
        if (index >= targets.length) return;
        const tenancyId = targets[index];
        const key = checkKey(y, m, tenancyId);
        try {
          const data = await fetchBexioDraftPreflight({
            tenancyId,
            year: y,
            month: m,
          });
          if (gen !== checkGen.current) return;
          writeCheck(key, { state: "done", result: data });
          if (data?.ready) ready += 1;
          else blocked += 1;
        } catch (e) {
          if (gen !== checkGen.current) return;
          writeCheck(key, {
            state: "error",
            error: e?.message || "Prüfung fehlgeschlagen.",
          });
          failed += 1;
        } finally {
          if (gen === checkGen.current) {
            setCheckRun((p) => (p ? { ...p, done: p.done + 1 } : p));
          }
        }
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(CHECK_CONCURRENCY, targets.length) }, worker)
    );

    if (gen !== checkGen.current) return;
    setCheckRun(null);
    const checked = ready + blocked + failed;
    toast.success(
      `${checked} ${checked === 1 ? "Entwurf" : "Entwürfe"} geprüft: ` +
        `${ready} bereit, ${blocked} blockiert` +
        (failed ? `, ${failed} Prüfung fehlgeschlagen` : "") +
        "."
    );
  }, [year, month, previewRows, writeCheck]);

  // ── counters (whole month, never search-filtered) ──────────────────
  const counts = useMemo(() => {
    const c = {
      [ROW_STATUS.NOT_CHECKED]: 0,
      [ROW_STATUS.CHECKING]: 0,
      [ROW_STATUS.READY]: 0,
      [ROW_STATUS.BLOCKED]: 0,
      [ROW_STATUS.CREATED]: 0,
      [ROW_STATUS.FAILED]: 0,
      [ROW_STATUS.CHECK_ERROR]: 0,
      [ROW_STATUS.REVIEW]: 0,
      [ROW_STATUS.UNKNOWN]: 0,
    };
    for (const r of allRows) {
      if (c[r.status] !== undefined) c[r.status] += 1;
    }
    // Rows a human has to look at. Counted together so none of them can hide.
    c.ATTENTION =
      c[ROW_STATUS.REVIEW] + c[ROW_STATUS.CHECK_ERROR] + c[ROW_STATUS.UNKNOWN];
    return c;
  }, [allRows]);

  const total = allRows.length;
  const pendingChecks = counts[ROW_STATUS.NOT_CHECKED];
  const checkedCount = total - pendingChecks - counts[ROW_STATUS.CHECKING];

  const steps = useMemo(() => {
    const created = counts[ROW_STATUS.CREATED];
    const open = total - created;
    return [
      {
        title: "Kandidaten",
        value: String(total),
        state: total ? "done" : "idle",
      },
      {
        title: "Geprüft",
        value: `${checkedCount}/${total}`,
        state:
          total && checkedCount === total
            ? "done"
            : checkedCount
            ? "active"
            : "idle",
      },
      {
        title: "Bereit",
        value: String(counts[ROW_STATUS.READY]),
        state: counts[ROW_STATUS.READY] ? "active" : "idle",
      },
      {
        title: "Erstellt",
        value: String(created),
        state: total && created === total ? "done" : created ? "active" : "idle",
      },
      {
        title: "Offen",
        value: String(open),
        state: total && open === 0 ? "done" : "active",
      },
    ];
  }, [counts, total, checkedCount]);

  // Looked up in the UNFILTERED list on purpose: `rows` is narrowed by the search
  // box, so searching while a row is selected would hand the panel an undefined row —
  // and with it an undefined documentNr, i.e. a create button armed over an invoice
  // that already exists.
  const selectedRow = allRows.find((r) => r.tenancyId === selectedId) || null;
  const selectedEntry = selectedId
    ? checks[checkKey(year, month, selectedId)]
    : undefined;

  // ── create ─────────────────────────────────────────────────────────
  const handleConfirmCreate = useCallback(async () => {
    if (inFlight.current) return; // exactly one POST per intent
    if (!confirmRow) return;
    if (!canCreate) return; // admin + complete ledger, re-checked at the last moment

    const y = year;
    const m = month;
    const tenancyId = confirmRow.tenancyId;

    inFlight.current = true;
    setCreating(true);
    try {
      const res = await createBexioInvoiceDraft({ tenancyId, year: y, month: m });
      const nr = res?.document_nr;
      toast.success(
        nr ? `Bexio-Entwurf ${nr} wurde erstellt.` : "Bexio-Entwurf wurde erstellt."
      );
    } catch (e) {
      if (e instanceof BexioDraftCreateError) {
        const detail =
          (e.blockers?.length && blockerText(e.blockers[0])) ||
          (e.reason && blockerText(e.reason)) ||
          e.message;
        toast.error(`Entwurf nicht erstellt: ${detail}`);
      } else {
        toast.error(e?.message || "Entwurf konnte nicht erstellt werden.");
      }
    } finally {
      setConfirmRow(null);

      // Drop the preflight for this tenancy+month on EVERY outcome, success or
      // failure. This is the fail-closed half of the no-retry rule and it matters
      // most exactly where it is least obvious: on `bexio_create_indeterminate` a
      // real invoice may already exist in Bexio, and the stale `ready: true` from
      // before the attempt would otherwise re-arm both create buttons and let one
      // more click fire a second real POST. Without a result the row falls back to
      // NOT_CHECKED, where nothing is armed, and the reloaded ledger below is what
      // decides what really happened. There is no automatic retry on any path.
      //
      // Deleting the key is necessary but NOT sufficient: a preflight issued before
      // this attempt could still be in flight and would write its pre-attempt
      // `ready: true` straight back into the slot we just cleared. Bumping the
      // generation first makes every outstanding response a no-op.
      //
      // This is deliberately blunt: it abandons a sweep that happens to be running
      // (reachable — a row can resolve to READY and be created while later rows are
      // still queued). The cost is re-running the sweep; the alternative is
      // per-key generations, which buy convenience at the price of a subtler
      // invariant in the one place where being wrong means a duplicate invoice.
      checkGen.current += 1;
      setCheckRun(null);
      setChecks((prev) => {
        const next = {};
        const dead = checkKey(y, m, tenancyId);
        for (const [k, v] of Object.entries(prev)) {
          if (k === dead || v?.state === "checking") continue;
          next[k] = v;
        }
        return next;
      });

      setCreating(false);
      inFlight.current = false;
      await loadMonth();
    }
  }, [confirmRow, canCreate, year, month, loadMonth]);

  // A single row's check counts as "busy" too, otherwise the sweep button stays live
  // during one and both requests race for the same slot.
  const checking =
    checkRun !== null ||
    allRows.some((r) => r.status === ROW_STATUS.CHECKING);

  return (
    <div className="min-h-full bg-background p-4 sm:p-6">
      {/* No page heading here: BexioLayout draws "Bexio" once for the whole
          workspace, and the active tab already says which page this is. This is
          the ONLY change UI-1 made to this file — presentation, no workflow. */}
      {loadError && (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
          <span>{loadError}</span>
          <button
            type="button"
            onClick={loadMonth}
            className="rounded-lg border border-red-300 px-3 py-1 text-xs font-medium dark:border-red-500/40"
          >
            Erneut versuchen
          </button>
        </div>
      )}

      {ledgerIncomplete && !loadError && (
        <div
          role="alert"
          className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200"
        >
          Ledger-Daten unvollständig. Rechnungsentwürfe können derzeit nicht sicher
          erstellt werden.
        </div>
      )}

      <BexioDraftSummaryCards counts={counts} pendingChecks={pendingChecks} />

      <div className="mt-4">
        <BexioDraftMonthWorkflow
          year={year}
          month={month}
          onMonthChange={(y, m) => {
            // Abandon anything in flight for the month we are leaving, and drop every
            // piece of state that is about to describe the wrong period.
            //
            // The rows, the ledger and the ledger-completeness flag are NOT keyed by
            // month, and they are only ever rewritten when the new month's fetches
            // resolve. Leaving them in place means that for the whole duration of
            // that load the header says October while the table shows September's
            // candidates — including September's document numbers and CREATED
            // badges under an October heading. That is not a race; it is every
            // single month switch. Clearing them costs one skeleton frame and makes
            // the screen unable to state something false.
            abandonChecksInFlight();
            setSelectedId(null);
            setConfirmRow(null);
            setPreviewRows([]);
            setLedgerByTenancy({});
            setLedgerIncomplete(true); // unknown ledger is not a complete ledger
            setLoading(true);
            setYear(y);
            setMonth(m);
          }}
          steps={steps}
          onRefresh={loadMonth}
          onCheckAll={checkAll}
          loading={loading}
          checking={checking}
          checkProgress={checkRun}
          creating={creating}
          canCreate={canCreate}
          createBlockedReason={createBlockedReason}
          candidateCount={total}
        />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-3">
        <div className="xl:col-span-2">
          <BexioDraftTable
            rows={rows}
            year={year}
            month={month}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onCreate={(row) => {
              setSelectedId(row.tenancyId);
              setConfirmRow(row);
            }}
            onCheckProvider={(row) => {
              setSelectedId(row.tenancyId);
              providerMissing.checkProvider(row);
            }}
            onReplace={(row) => {
              setSelectedId(row.tenancyId);
              providerMissing.openReplace(row);
            }}
            canCreate={canCreate}
            createBlockedReason={createBlockedReason}
            busyTenancyId={
              creating
                ? confirmRow?.tenancyId
                : providerMissing.checkingId ||
                  (providerMissing.replacing
                    ? providerMissing.replaceRow?.tenancyId
                    : null)
            }
            search={search}
            onSearchChange={setSearch}
            loading={loading}
          />
        </div>

        <div className="xl:sticky xl:top-4 xl:self-start">
          <BexioDraftPreviewPanel
            preflight={selectedEntry?.state === "done" ? selectedEntry.result : null}
            hasSelection={Boolean(selectedId)}
            checking={selectedEntry?.state === "checking"}
            error={selectedEntry?.state === "error" ? selectedEntry.error : ""}
            status={selectedRow?.status}
            documentNr={selectedRow?.documentNr}
            onCreate={() => selectedRow && setConfirmRow(selectedRow)}
            onCheck={() => selectedId && checkOne(selectedId)}
            canCreate={canCreate}
            createBlockedReason={createBlockedReason}
            creating={creating}
          />
        </div>
      </div>

      <BexioDraftCreateConfirmModal
        open={Boolean(confirmRow)}
        row={confirmRow}
        preflight={
          confirmRow
            ? checks[checkKey(year, month, confirmRow.tenancyId)]?.result || null
            : null
        }
        year={year}
        month={month}
        submitting={creating}
        onCancel={() => setConfirmRow(null)}
        onConfirm={handleConfirmCreate}
      />

      <BexioDraftReplaceConfirmModal
        open={Boolean(providerMissing.replaceRow)}
        row={providerMissing.replaceRow}
        year={year}
        month={month}
        submitting={providerMissing.replacing}
        onCancel={providerMissing.cancelReplace}
        onConfirm={providerMissing.submitReplace}
      />
    </div>
  );
}
